/**
 * Customer Algo Control (One-Click Activation) API service.
 *
 * Talks only to `/customer/algo/*` -- a customer-scoped surface, NOT the
 * admin-only `/algo/admin/*` router. Every call is implicitly scoped to
 * the current authenticated user by the backend (via the JWT), same
 * convention as `brokerAccountService`/`portfolioService` -- there is no
 * `user_id` parameter anywhere here, and no method here can ever read or
 * affect another user's activation.
 *
 * `activate`/`deactivate` are both idempotent on the backend: calling
 * either repeatedly (double-click, refresh, retry after a dropped
 * response) never creates a duplicate activation or errors out -- it just
 * returns the current state. Neither ever prompts for confirmation here
 * or on the backend, by design.
 */
import api from "@/services/api";
import type { CustomerAlgoActivationListResponse, CustomerAlgoActivationRead, DeactivateResponse } from "@/types";

export const customerAlgoService = {
  /** `GET /customer/algo/activations` -- every algo the current user has
   * ever activated (any status), scoped to them alone. */
  async listActivations(): Promise<CustomerAlgoActivationListResponse> {
    const { data } = await api.get<CustomerAlgoActivationListResponse>("/customer/algo/activations");
    return data;
  },

  /** `GET /customer/algo/{algo_id}/status` -- current user's activation
   * status for one algo. Returns a synthetic STOPPED response (not
   * persisted) if they've never activated it. */
  async getStatus(algoId: string): Promise<CustomerAlgoActivationRead> {
    const { data } = await api.get<CustomerAlgoActivationRead>(`/customer/algo/${algoId}/status`);
    return data;
  },

  /** `POST /customer/algo/{algo_id}/activate` -- idempotent. Requires the
   * current user to already have a CONNECTED broker account marked as
   * their default (422 otherwise). No confirmation step. */
  async activate(algoId: string): Promise<CustomerAlgoActivationRead> {
    const { data } = await api.post<CustomerAlgoActivationRead>(`/customer/algo/${algoId}/activate`);
    return data;
  },

  /** `POST /customer/algo/{algo_id}/deactivate` -- idempotent (safe to call
   * repeatedly/concurrently, e.g. a double-click: never places a duplicate
   * broker order). 2026-09-08 on, this also immediately attempts to
   * flatten every open position for this user/algo, broker-reconciled for
   * a LIVE position -- `flatten_summary.all_flat` tells the caller whether
   * that's CONFIRMED, not merely attempted. No confirmation step. */
  async deactivate(algoId: string): Promise<DeactivateResponse> {
    const { data } = await api.post<DeactivateResponse>(`/customer/algo/${algoId}/deactivate`);
    return data;
  },
};

export default customerAlgoService;
